
import React, { useRef } from "react";

import { useParams, useNavigate } from "react-router-dom";

import html2pdf from "html2pdf.js";

import { useGetInvoiceDetailsQuery } from "../../slices/billingApiSlice";

const InvoiceDetailsScreen = () => {
  const { invoiceId } = useParams();

  const navigate = useNavigate();

  const invoiceRef = useRef(null);

  const { data, isLoading, error } = useGetInvoiceDetailsQuery(invoiceId);

  const invoice = data?.data;

  // =====================================================
  // Loading
  // =====================================================

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div
          className="
            animate-spin
            h-10
            w-10
            border-4
            border-blue-600
            border-t-transparent
            rounded-full
          "
        />
      </div>
    );
  }

  // =====================================================
  // Error
  // =====================================================

  if (error || !invoice) {
    return (
      <div
        className="
          bg-red-50
          border
          border-red-200
          text-red-700
          rounded-lg
          p-5
          m-5
        "
      >
        {error?.data?.message || error?.error || "Invoice not found"}
      </div>
    );
  }

  // =====================================================
  // Helpers
  // =====================================================

  const formatDate = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatCurrency = (amount = 0) => {
    return `₹${Number(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatStatus = (status) => {
    if (!status) return "-";

    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const formatDiscountType = (type) => {
    if (!type) return "-";

    if (type === "percentage") return "Percentage";

    if (type === "fixed") return "Fixed Amount";

    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const formatPaymentMethod = (method) => {
    if (!method) return "-";

    return method
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // =====================================================
  // Actions
  // =====================================================

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    const options = {
      margin: 0.4,

      filename: `${invoice.invoiceNumber}.pdf`,

      image: {
        type: "jpeg",
        quality: 1,
      },

      html2canvas: {
        scale: 2,
        useCORS: true,
      },

      jsPDF: {
        unit: "in",
        format: "a4",
        orientation: "portrait",
      },
    };

    html2pdf().set(options).from(invoiceRef.current).save();
  };

  // =====================================================
  // Status Style
  // =====================================================

  const statusStyle = (status) => {
    switch (status) {
      case "paid":
        return "bg-green-100 text-green-700";

      case "cancelled":
        return "bg-red-100 text-red-700";

      case "overdue":
        return "bg-red-100 text-red-700";

      default:
        return "bg-yellow-100 text-yellow-700";
    }
  };

  // =====================================================
  // GST Helpers
  // =====================================================

  const isCgstSgst = invoice.gstType === "cgst_sgst";

  const isIgst = invoice.gstType === "igst";

  // =====================================================
  // Balance
  // =====================================================

  const balanceAmount = Math.max(
    Number(invoice.total || 0) - Number(invoice.paidAmount || 0),
    0,
  );

  // =====================================================
  // Billing Settings / Discount
  // =====================================================

  /*
   * The backend may expose the applied center-level
   * billing settings in one of these locations.
   *
   * Preferred:
   *   invoice.discountSettings
   *
   * Also supported:
   *   invoice.billingSettings.discount
   *   invoice.discount
   *
   * The actual invoice discountAmount remains the source
   * of truth for the amount deducted from the invoice.
   */

  const discountSettings =
    invoice.discountSettings ||
    invoice.billingSettings?.discount ||
    invoice.discount ||
    null;

  const discountEnabled =
    discountSettings?.enabled === true ||
    Number(invoice.discountAmount || 0) > 0;

  const discountType = discountSettings?.type;

  const discountConfiguredValue = Number(discountSettings?.value || 0);

  const discountMaxAmount =
    discountSettings?.maxAmount !== null &&
    discountSettings?.maxAmount !== undefined
      ? Number(discountSettings.maxAmount)
      : null;

  const discountReason = discountSettings?.reason;

  const discountValidFrom = discountSettings?.validFrom;

  const discountValidUntil = discountSettings?.validUntil;

  const discountAmount = Number(invoice.discountAmount || 0);

  // =====================================================
  // Render
  // =====================================================

  return (
    <div className="p-4 md:p-6">
      {/* =================================================
          ACTIONS
      ================================================= */}

      <div
        className="
          flex
          justify-end
          gap-3
          mb-5
          no-print
        "
      >
        <button
          onClick={handlePrint}
          className="
            bg-gray-700
            hover:bg-gray-800
            text-white
            px-5
            py-2
            rounded-lg
          "
        >
          🖨 Print
        </button>

        <button
          onClick={handleDownloadPDF}
          className="
            bg-blue-600
            hover:bg-blue-700
            text-white
            px-5
            py-2
            rounded-lg
          "
        >
          📄 Download PDF
        </button>
      </div>

      {/* =================================================
          INVOICE
      ================================================= */}

      <div
        ref={invoiceRef}
        className="
          bg-white
          rounded-xl
          shadow
          p-6
          md:p-8
        "
      >
        {/* =================================================
            HEADER
        ================================================= */}

        <div
          className="
            flex
            flex-col
            md:flex-row
            justify-between
            items-start
            gap-4
            mb-8
          "
        >
          <div>
            <h1
              className="
                text-3xl
                font-bold
                text-blue-600
              "
            >
              Invoice
            </h1>

            <p className="text-gray-600 mt-1">{invoice.invoiceNumber}</p>
          </div>

          <span
            className={`
              px-4
              py-2
              rounded-full
              font-semibold
              ${statusStyle(invoice.status)}
            `}
          >
            {formatStatus(invoice.status)}
          </span>
        </div>

        {/* =================================================
            CUSTOMER / BILLING INFORMATION
        ================================================= */}

        <Section title="Billing Information">
          <div
            className="
              grid
              md:grid-cols-2
              gap-6
            "
          >
            {/* Customer */}

            <div>
              <p className="font-semibold text-lg">
                {invoice.coachingCenter?.name ||
                  invoice.coachingCenterName ||
                  "-"}
              </p>

              {invoice.phone && (
                <p className="text-gray-600 mt-1">Phone: {invoice.phone}</p>
              )}

              {invoice.email && (
                <p className="text-gray-600">Email: {invoice.email}</p>
              )}
            </div>

            {/* Billing Address */}

            <div>
              <p className="font-semibold">Billing Address</p>

              <div className="mt-2 text-gray-600 leading-6">
                {invoice.address?.addressLine1 && (
                  <>
                    {invoice.address.addressLine1}
                    <br />
                  </>
                )}

                {invoice.address?.addressLine2 && (
                  <>
                    {invoice.address.addressLine2}
                    <br />
                  </>
                )}

                {(invoice.address?.city || invoice.address?.district) && (
                  <>
                    {[invoice.address?.city, invoice.address?.district]
                      .filter(Boolean)
                      .join(", ")}
                    <br />
                  </>
                )}

                {invoice.address?.state && (
                  <>
                    {invoice.address.state}

                    {invoice.address?.pincode
                      ? ` - ${invoice.address.pincode}`
                      : ""}

                    <br />
                  </>
                )}

                {invoice.address?.country && <>{invoice.address.country}</>}
              </div>
            </div>
          </div>
        </Section>

        {/* =================================================
            INVOICE INFORMATION
        ================================================= */}

        <Section title="Invoice Information">
          <div
            className="
              grid
              md:grid-cols-2
              gap-5
            "
          >
            <InfoItem label="Invoice Number" value={invoice.invoiceNumber} />

            <InfoItem
              label="Invoice Date"
              value={formatDate(invoice.invoiceDate)}
            />

            <InfoItem label="Due Date" value={formatDate(invoice.dueDate)} />

            <div>
              <p className="font-semibold">Billing Period</p>

              <p className="text-gray-600 mt-1">
                {formatDate(invoice.billingPeriodStart)}

                {" - "}

                {formatDate(invoice.billingPeriodEnd)}
              </p>
            </div>
          </div>
        </Section>

        {/* =================================================
            USAGE SNAPSHOT
        ================================================= */}

        <Section title="Usage Snapshot">
          <div
            className="
              grid
              grid-cols-1
              md:grid-cols-2
              gap-5
            "
          >
            <Metric label="Center Admins" value={invoice.adminCount} />

            <Metric
              label="Active Students"
              value={invoice.activeStudentCount}
            />
          </div>
        </Section>

        {/* =================================================
            BILLING ITEMS
        ================================================= */}

        <Section title="Billing Items">
          <div className="overflow-x-auto">
            <table
              className="
                min-w-full
                border
                border-collapse
              "
            >
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-3 border text-left">Description</th>

                  <th className="p-3 border text-center">Qty</th>

                  <th className="p-3 border text-right">Unit Price</th>

                  <th className="p-3 border text-right">Amount</th>
                </tr>
              </thead>

              <tbody>
                {invoice.lineItems?.length > 0 ? (
                  invoice.lineItems.map((item, index) => (
                    <tr key={item._id || index}>
                      <td className="p-3 border">{item.description}</td>

                      <td className="p-3 border text-center">
                        {item.quantity}
                      </td>

                      <td className="p-3 border text-right">
                        {formatCurrency(item.unitPrice)}
                      </td>

                      <td className="p-3 border text-right">
                        {formatCurrency(item.amount)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="4"
                      className="
                        p-5
                        border
                        text-center
                        text-gray-500
                      "
                    >
                      No billing items found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Section>

        {/* =================================================
            CENTER BILLING SETTINGS / DISCOUNT
        ================================================= */}

        <Section title="Center Billing Settings">
          <div
            className="
              border
              border-gray-200
              rounded-xl
              overflow-hidden
            "
          >
            <div
              className="
                bg-gray-50
                px-4
                py-3
                border-b
                flex
                flex-col
                sm:flex-row
                sm:items-center
                sm:justify-between
                gap-2
              "
            >
              <div>
                <p className="font-semibold text-gray-800">
                  Discount Configuration
                </p>

                <p className="text-xs text-gray-500 mt-1">
                  Coaching-center level billing setting
                </p>
              </div>

              <span
                className={`
                  inline-flex
                  w-fit
                  px-3
                  py-1
                  rounded-full
                  text-xs
                  font-semibold
                  ${
                    discountEnabled
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-100 text-gray-600"
                  }
                `}
              >
                {discountEnabled ? "Discount Enabled" : "No Discount"}
              </span>
            </div>

            <div className="p-4">
              {discountEnabled ? (
                <div
                  className="
                    grid
                    grid-cols-1
                    sm:grid-cols-2
                    lg:grid-cols-3
                    gap-5
                  "
                >
                  <InfoItem
                    label="Discount Type"
                    value={formatDiscountType(discountType)}
                  />

                  <InfoItem
                    label="Configured Value"
                    value={
                      discountType === "percentage"
                        ? `${discountConfiguredValue}%`
                        : formatCurrency(discountConfiguredValue)
                    }
                  />

                  {discountType === "percentage" && (
                    <InfoItem
                      label="Maximum Discount"
                      value={
                        discountMaxAmount !== null
                          ? formatCurrency(discountMaxAmount)
                          : "No maximum"
                      }
                    />
                  )}

                  <InfoItem
                    label="Applied Discount"
                    value={formatCurrency(discountAmount)}
                  />

                  <InfoItem
                    label="Valid From"
                    value={formatDate(discountValidFrom)}
                  />

                  <InfoItem
                    label="Valid Until"
                    value={formatDate(discountValidUntil)}
                  />

                  {discountReason && (
                    <div className="sm:col-span-2 lg:col-span-3">
                      <p className="font-semibold">Reason</p>

                      <p className="text-gray-600 mt-1">{discountReason}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-gray-500">
                  No center-level discount was applied to this invoice.
                </div>
              )}
            </div>
          </div>
        </Section>

        {/* =================================================
            AMOUNT SUMMARY
        ================================================= */}

        <Section title="Amount Summary">
          <div className="max-w-md ml-auto space-y-3">
            <AmountRow
              label="Calculated Subtotal"
              value={invoice.calculatedSubtotal ?? invoice.subtotal}
            />

            <AmountRow
              label="Center Discount"
              value={discountAmount}
              negative
            />

            {discountEnabled && discountAmount > 0 && (
              <div
                className="
                    bg-green-50
                    border
                    border-green-200
                    rounded-lg
                    p-3
                    text-sm
                    text-green-700
                  "
              >
                <div className="flex justify-between gap-4">
                  <span>
                    {discountType === "percentage"
                      ? `Discount (${discountConfiguredValue}%)`
                      : "Fixed Discount"}
                  </span>

                  <strong>- {formatCurrency(discountAmount)}</strong>
                </div>

                {discountReason && (
                  <p className="mt-1 text-xs text-green-600">
                    {discountReason}
                  </p>
                )}
              </div>
            )}

            {invoice.minimumInvoiceApplied && (
              <div
                className="
                  flex
                  justify-between
                  items-center
                  text-sm
                  text-orange-600
                  bg-orange-50
                  rounded-lg
                  px-3
                  py-2
                "
              >
                <span>Minimum Invoice Applied</span>

                <span className="font-semibold">
                  {formatCurrency(invoice.taxableAmount)}
                </span>
              </div>
            )}

            <AmountRow label="Taxable Amount" value={invoice.taxableAmount} />
          </div>
        </Section>

        {/* =================================================
            GST SUMMARY
        ================================================= */}

        <Section title="GST Summary">
          <div className="max-w-md ml-auto space-y-3">
            <AmountRow
              label={`GST (${invoice.gstRate || 0}%)`}
              value={invoice.totalTaxAmount}
            />

            {/* CGST + SGST */}

            {isCgstSgst && (
              <>
                <AmountRow
                  label={`CGST (${invoice.cgstRate || 0}%)`}
                  value={invoice.cgstAmount}
                />

                <AmountRow
                  label={`SGST (${invoice.sgstRate || 0}%)`}
                  value={invoice.sgstAmount}
                />
              </>
            )}

            {/* IGST */}

            {isIgst && (
              <AmountRow
                label={`IGST (${invoice.igstRate || 0}%)`}
                value={invoice.igstAmount}
              />
            )}

            {/* No GST */}

            {invoice.gstType === "none" && (
              <p className="text-sm text-gray-500">No GST applicable.</p>
            )}
          </div>
        </Section>

        {/* =================================================
            FINAL TOTAL
        ================================================= */}

        <div
          className="
            border-t
            mt-4
            pt-5
          "
        >
          <div className="max-w-md ml-auto">
            <AmountRow
              label="Total Amount"
              value={invoice.total}
              highlight
              large
            />
          </div>
        </div>

        {/* =================================================
            PAYMENT SUMMARY
        ================================================= */}

        <Section title="Payment Information">
          <div className="max-w-md ml-auto space-y-3">
            <AmountRow label="Invoice Total" value={invoice.total} />

            <AmountRow label="Paid Amount" value={invoice.paidAmount} />

            <AmountRow
              label="Balance"
              value={balanceAmount}
              highlight={balanceAmount > 0}
            />

            {invoice.paymentMethod && (
              <div className="flex justify-between gap-4">
                <span className="font-semibold">Payment Method</span>

                <span className="text-gray-600 text-right">
                  {formatPaymentMethod(invoice.paymentMethod)}
                </span>
              </div>
            )}

            {invoice.paymentReference && (
              <div className="flex justify-between gap-4">
                <span className="font-semibold">Payment Reference</span>

                <span className="text-gray-600 text-right">
                  {invoice.paymentReference}
                </span>
              </div>
            )}

            {invoice.paidAt && (
              <div className="flex justify-between gap-4">
                <span className="font-semibold">Paid At</span>

                <span className="text-gray-600 text-right">
                  {formatDate(invoice.paidAt)}
                </span>
              </div>
            )}

            <div className="flex justify-between items-center gap-4">
              <span className="font-semibold">Status</span>

              <span
                className={`
                  px-3
                  py-1
                  rounded-full
                  text-sm
                  font-semibold
                  ${statusStyle(invoice.status)}
                `}
              >
                {formatStatus(invoice.status)}
              </span>
            </div>
          </div>
        </Section>

        {/* =================================================
            CANCELLATION
        ================================================= */}

        {invoice.status === "cancelled" && (
          <Section title="Cancellation">
            <div className="bg-red-50 rounded-lg p-4">
              <p className="text-red-700">
                <b>Cancelled At:</b> {formatDate(invoice.cancelledAt)}
              </p>

              <p className="text-red-700 mt-1">
                <b>Reason:</b>{" "}
                {invoice.cancellationReason || "No reason provided"}
              </p>
            </div>
          </Section>
        )}

        {/* =================================================
            FOOTER ACTIONS
        ================================================= */}

        <div
          className="
            flex
            justify-end
            mt-8
            no-print
          "
        >
          <button
            onClick={() => navigate(-1)}
            className="
              bg-gray-600
              hover:bg-gray-700
              text-white
              px-5
              py-2
              rounded-lg
            "
          >
            Back
          </button>
        </div>
      </div>
    </div>
  );
};

// ======================================================
// Section
// ======================================================

const Section = ({ title, children }) => {
  return (
    <section className="mb-8">
      <h2
        className="
          text-lg
          font-semibold
          text-gray-800
          mb-4
        "
      >
        {title}
      </h2>

      {children}
    </section>
  );
};

// ======================================================
// Info Item
// ======================================================

const InfoItem = ({ label, value }) => {
  return (
    <div>
      <p className="font-semibold">{label}</p>

      <p className="text-gray-600 mt-1">{value || "-"}</p>
    </div>
  );
};

// ======================================================
// Metric
// ======================================================

const Metric = ({ label, value }) => {
  return (
    <div
      className="
        rounded-lg
        bg-gray-50
        border
        border-gray-200
        p-4
      "
    >
      <p className="text-sm text-gray-500">{label}</p>

      <h3
        className="
          text-2xl
          font-bold
          text-gray-800
          mt-1
        "
      >
        {value ?? 0}
      </h3>
    </div>
  );
};

// ======================================================
// Amount Row
// ======================================================

const AmountRow = ({
  label,
  value,
  highlight = false,
  negative = false,
  large = false,
}) => {
  const numericValue = Number(value || 0);

  const formattedValue = `₹${numericValue.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

  return (
    <div
      className={`
        flex
        justify-between
        items-center
        gap-4
        ${highlight ? "font-bold text-lg" : ""}
      `}
    >
      <span>{label}</span>

      <span
        className={`
          whitespace-nowrap
          ${negative ? "text-red-600" : ""}
          ${large ? "text-xl" : ""}
        `}
      >
        {negative && numericValue > 0 ? `- ${formattedValue}` : formattedValue}
      </span>
    </div>
  );
};

export default InvoiceDetailsScreen;
